import { Timestamp } from '@bufbuild/protobuf';
import { Button, Empty, Modal, Radio, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  DiagnocoinLedgerEntry,
  DiagnocoinLedgerEntryType,
} from 'protocol/api/billing_new/dto_diagnocoin_new_pb';

import { i18n as billingI18n } from '@/entities/billing';

import {
  DIAGNOCOIN_DATE_TIME_FORMAT,
  diagnocoinHistoryFilterMessages,
  DiagnocoinHistoryFilter,
  diagnocoinsMessages,
} from '../../config';
import { useDiagnocoinLedger } from '../../hooks';

const { Text } = Typography;

type DiagnocoinHistoryModalProps = {
  accountID: string;
  open: boolean;
  onCancel: () => void;
};

export const DiagnocoinHistoryModal = (props: DiagnocoinHistoryModalProps) => {
  const { accountID, open, onCancel } = props;

  const { formatMessage, formatDate } = useIntl();

  const [filter, setFilter] = useState(DiagnocoinHistoryFilter.all);

  const { entries, isLoading, hasFailed, hasMore, loadMore } =
    useDiagnocoinLedger({ accountID, filter, isOpen: open });

  useEffect(() => {
    if (open) {
      setFilter(DiagnocoinHistoryFilter.all);
    }
  }, [open]);

  const formatEntryDate = (timestamp?: Timestamp) =>
    timestamp
      ? formatDate(timestamp.toDate(), DIAGNOCOIN_DATE_TIME_FORMAT)
      : '—';

  const renderChange = (entry: DiagnocoinLedgerEntry) => {
    const delta = Number(entry.Deltas?.Available ?? 0);

    if (delta === 0) {
      return <Text type="secondary">—</Text>;
    }

    return (
      <Text type={delta > 0 ? 'success' : 'danger'}>
        {delta > 0 ? `+${delta}` : delta}
      </Text>
    );
  };

  const columns: ColumnsType<DiagnocoinLedgerEntry> = [
    {
      title: formatMessage(diagnocoinsMessages.historyDate),
      key: 'date',
      render: (_: unknown, entry: DiagnocoinLedgerEntry) =>
        formatEntryDate(entry.EffectiveAt),
    },
    {
      title: formatMessage(diagnocoinsMessages.historyType),
      key: 'type',
      render: (_: unknown, entry: DiagnocoinLedgerEntry) =>
        formatMessage(
          billingI18n.diagnocoinLedgerEntryTypeMessages[
            entry.Type as DiagnocoinLedgerEntryType
          ] ??
            billingI18n.diagnocoinLedgerEntryTypeMessages[
              DiagnocoinLedgerEntryType.DiagnocoinLedgerEntryTypeInvalidValue
            ],
        ),
    },
    {
      title: formatMessage(diagnocoinsMessages.historyChange),
      key: 'change',
      render: (_: unknown, entry: DiagnocoinLedgerEntry) => renderChange(entry),
    },
    {
      title: formatMessage(diagnocoinsMessages.historyAvailableAfter),
      key: 'availableAfter',
      render: (_: unknown, entry: DiagnocoinLedgerEntry) =>
        Number(entry.AvailableAfter ?? 0),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={760}
      title={formatMessage(diagnocoinsMessages.historyTitle)}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Radio.Group
          value={filter}
          buttonStyle="solid"
          onChange={(event) => setFilter(event.target.value)}
        >
          {Object.values(DiagnocoinHistoryFilter).map((filterValue) => (
            <Radio.Button key={filterValue} value={filterValue}>
              {formatMessage(diagnocoinHistoryFilterMessages[filterValue])}
            </Radio.Button>
          ))}
        </Radio.Group>

        <Table<DiagnocoinLedgerEntry>
          rowKey="ID"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={entries}
          pagination={false}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={formatMessage(
                  hasFailed
                    ? diagnocoinsMessages.historyFailed
                    : diagnocoinsMessages.historyEmpty,
                )}
              />
            ),
          }}
        />

        {hasMore && (
          <Button type="text" loading={isLoading} onClick={loadMore}>
            <FormattedMessage id="global.showMore" defaultMessage="Show more" />
          </Button>
        )}
      </Space>
    </Modal>
  );
};
